import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  Check,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Download,
  FolderOpen,
  Search,
  Share2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreateShareDialog } from "@/components/document-sharing/CreateShareDialog";
import { ErrorState } from "@/components/shared/error-state";
import { FileIcon, formatFileSize } from "@/components/shared/file-display";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  type OrgFileRow,
  type OrgFileSortField,
  type OrgFolderAggregate,
  type OrgFolderDrilldownRow,
  useOrgFiles,
  useOrgFolderDrilldown,
  useOrgFolders,
} from "@/hooks/use-org-files";
import { useProjects } from "@/hooks/use-projects";
import { cn } from "@/lib/utils";

// ── Helpers ──────────────────────────────────────────────────────────────────

function isPreviewable(mimeType: string | null): "image" | "pdf" | null {
  if (!mimeType) return null;
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  return null;
}

function getFileTypeLabel(mimeType: string | null): string {
  if (!mimeType) return "—";
  if (mimeType === "application/pdf") return "PDF";
  if (mimeType.startsWith("image/")) return "Image";
  const lm = mimeType.toLowerCase();
  if (lm.includes("spreadsheet") || lm.includes("excel") || lm.includes("csv"))
    return "Spreadsheet";
  if (
    lm.includes("word") ||
    lm.includes("document") ||
    lm.includes("msword") ||
    lm.includes("officedocument")
  )
    return "Document";
  if (lm.includes("powerpoint") || lm.includes("presentation")) return "Presentation";
  return mimeType;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Sortable column header ────────────────────────────────────────────────────

function SortableHeader<T extends string>({
  label,
  field,
  current,
  dir,
  onSort,
}: {
  label: string;
  field: T;
  current: T;
  dir: "asc" | "desc";
  onSort: (f: T) => void;
}) {
  const isActive = current === field;
  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      className="flex items-center gap-1 font-medium hover:text-foreground"
    >
      {label}
      {isActive ? (
        dir === "asc" ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )
      ) : null}
    </button>
  );
}

// ── Preview dialog ────────────────────────────────────────────────────────────

interface PreviewState {
  id: string;
  fileName: string;
  mimeType: string | null;
  blobUrl: string | null;
  loading: boolean;
  error: string | null;
}

function FilePreviewDialog({
  preview,
  onClose,
}: {
  preview: PreviewState | null;
  onClose: () => void;
}) {
  function download() {
    if (!preview) return;
    window.location.href = `/api/files/${preview.id}`;
  }

  return (
    <Dialog open={preview !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[50vw] max-w-[50vw] sm:max-w-[50vw] h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {preview && <FileIcon mimeType={preview.mimeType} />}
            <span className="truncate">{preview?.fileName}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          {preview?.loading && <Skeleton className="h-full w-full" />}
          {preview?.error && (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <p className="text-sm text-destructive">{preview.error}</p>
              <Button variant="outline" size="sm" onClick={download}>
                <Download className="mr-2 h-4 w-4" /> Download instead
              </Button>
            </div>
          )}
          {preview?.blobUrl &&
            (isPreviewable(preview.mimeType) === "image" ? (
              <div className="flex h-full items-center justify-center overflow-auto bg-muted/30">
                <img
                  src={preview.blobUrl}
                  alt={preview.fileName}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (
              <iframe title={preview.fileName} src={preview.blobUrl} className="h-full w-full" />
            ))}
          {preview && !preview.loading && !preview.error && !preview.blobUrl && (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <p className="text-sm text-muted-foreground">
                Preview is not available for this file type.
              </p>
              <Button variant="outline" size="sm" onClick={download}>
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Multi-select dropdown ─────────────────────────────────────────────────────

function MultiSelectDropdown({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onToggle: (value: string) => void;
}) {
  const selectedSet = new Set(selected);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5 text-xs">
              {selected.length}
            </Badge>
          )}
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-60 overflow-y-auto min-w-[200px]">
        {options.length === 0 && (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">No options</div>
        )}
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onSelect={(e) => {
              e.preventDefault();
              onToggle(opt.value);
            }}
            className="gap-2"
          >
            <div className="flex h-4 w-4 items-center justify-center rounded border">
              {selectedSet.has(opt.value) && <Check className="h-3 w-3" />}
            </div>
            <span className="truncate">{opt.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Files tab toolbar ─────────────────────────────────────────────────────────

function FilesTabToolbar({
  searchInput,
  onSearchChange,
  projects,
  selectedProjectIds,
  onToggleProject,
  orgFolders,
  selectedFolderPaths,
  onToggleFolderPath,
  onClearAll,
}: {
  searchInput: string;
  onSearchChange: (v: string) => void;
  projects: Array<{ id: string; name: string }>;
  selectedProjectIds: string[];
  onToggleProject: (id: string) => void;
  orgFolders: OrgFolderAggregate[];
  selectedFolderPaths: string[];
  onToggleFolderPath: (path: string) => void;
  onClearAll: () => void;
}) {
  const projectOptions = projects.map((p) => ({ value: p.id, label: p.name }));
  const folderOptions = orgFolders.map((f) => ({
    value: f.fullPath,
    label: f.fullPath,
  }));

  const hasActiveFilters =
    selectedProjectIds.length > 0 || selectedFolderPaths.length > 0 || searchInput.length > 0;

  const projectMap = useMemo(() => new Map(projects.map((p) => [p.id, p.name])), [projects]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search files..."
            value={searchInput}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-8"
          />
        </div>
        <MultiSelectDropdown
          label="Project"
          options={projectOptions}
          selected={selectedProjectIds}
          onToggle={onToggleProject}
        />
        <MultiSelectDropdown
          label="Folder"
          options={folderOptions}
          selected={selectedFolderPaths}
          onToggle={onToggleFolderPath}
        />
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={onClearAll} className="text-muted-foreground">
            <X className="mr-1 h-3 w-3" />
            Clear all
          </Button>
        )}
      </div>

      {(selectedProjectIds.length > 0 || selectedFolderPaths.length > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {selectedProjectIds.map((id) => (
            <Badge key={id} variant="secondary" className="gap-1 pr-1">
              {projectMap.get(id) ?? id}
              <button
                type="button"
                onClick={() => onToggleProject(id)}
                className="ml-0.5 rounded hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {selectedFolderPaths.map((path) => (
            <Badge key={path} variant="outline" className="gap-1 pr-1">
              {path}
              <button
                type="button"
                onClick={() => onToggleFolderPath(path)}
                className="ml-0.5 rounded hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Files table ───────────────────────────────────────────────────────────────

function FilesTable({
  files,
  isLoading,
  isError,
  error,
  onRetry,
  sortBy,
  sortDir,
  onSort,
  sentinelRef,
  isFetchingNextPage,
  onOpenPreview,
  onShareFile,
}: {
  files: OrgFileRow[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  sortBy: OrgFileSortField;
  sortDir: "asc" | "desc";
  onSort: (f: OrgFileSortField) => void;
  sentinelRef: React.RefObject<HTMLTableRowElement | null>;
  isFetchingNextPage: boolean;
  onOpenPreview: (file: OrgFileRow) => void;
  onShareFile: (file: OrgFileRow) => void;
}) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {["ft-0", "ft-1", "ft-2", "ft-3", "ft-4", "ft-5", "ft-6", "ft-7"].map((k) => (
          <Skeleton key={k} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={onRetry} />;
  }

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-muted-foreground">No files found.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <SortableHeader
                label="File Name"
                field="fileName"
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
              />
            </TableHead>
            <TableHead className="w-24">Type</TableHead>
            <TableHead>
              <SortableHeader
                label="Project"
                field="projectName"
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
              />
            </TableHead>
            <TableHead>
              <SortableHeader
                label="Folder"
                field="folderPath"
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
              />
            </TableHead>
            <TableHead className="w-36">Uploaded By</TableHead>
            <TableHead className="w-28">
              <SortableHeader
                label="Date"
                field="createdAt"
                current={sortBy}
                dir={sortDir}
                onSort={onSort}
              />
            </TableHead>
            <TableHead className="w-20">Size</TableHead>
            <TableHead className="w-24 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.map((file, idx) => (
            <TableRow
              key={file.id}
              ref={idx === files.length - 1 ? sentinelRef : undefined}
              className="cursor-pointer hover:bg-muted/50"
              onClick={() => onOpenPreview(file)}
            >
              <TableCell>
                <div className="flex items-center gap-2">
                  <FileIcon mimeType={file.mimeType} />
                  <span className="truncate max-w-xs font-medium">{file.fileName}</span>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {getFileTypeLabel(file.mimeType)}
              </TableCell>
              <TableCell className="text-sm">{file.projectName ?? "—"}</TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {file.folderPath ?? "—"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {file.uploadedByName ?? "—"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(file.createdAt)}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatFileSize(file.fileSizeBytes)}
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!file.projectId}
                  onClick={(e) => {
                    e.stopPropagation();
                    onShareFile(file);
                  }}
                  title={
                    file.projectId ? "Share this document" : "Cannot share a file without a project"
                  }
                >
                  <Share2 className="mr-1 h-3.5 w-3.5" />
                  Share
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isFetchingNextPage && <Skeleton className="h-10 w-full rounded-none" />}
    </div>
  );
}

// ── Folder tree ───────────────────────────────────────────────────────────────

interface AggTreeNode {
  name: string;
  fullPath: string;
  fileCount: number;
  projectCount: number;
  children: AggTreeNode[];
  depth: number;
}

function buildAggTree(aggregates: OrgFolderAggregate[]): AggTreeNode[] {
  const nodeMap = new Map<string, AggTreeNode>();

  const sorted = [...aggregates].sort((a, b) => a.fullPath.localeCompare(b.fullPath));

  for (const agg of sorted) {
    const parts = agg.fullPath.split("/").filter(Boolean);
    const name = parts[parts.length - 1] ?? agg.fullPath;
    const depth = parts.length - 1;
    nodeMap.set(agg.fullPath, {
      name,
      fullPath: agg.fullPath,
      fileCount: agg.fileCount,
      projectCount: agg.projectCount,
      children: [],
      depth,
    });

    for (let i = 1; i < parts.length; i++) {
      const parentPath = parts.slice(0, i).join("/");
      if (!nodeMap.has(parentPath)) {
        const parentParts = parentPath.split("/");
        nodeMap.set(parentPath, {
          name: parentParts[parentParts.length - 1] ?? parentPath,
          fullPath: parentPath,
          fileCount: 0,
          projectCount: 0,
          children: [],
          depth: parentParts.length - 1,
        });
      }
    }
  }

  const roots: AggTreeNode[] = [];
  for (const node of nodeMap.values()) {
    const parts = node.fullPath.split("/").filter(Boolean);
    if (parts.length === 1) {
      roots.push(node);
    } else {
      const parentPath = parts.slice(0, -1).join("/");
      const parent = nodeMap.get(parentPath);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
  }

  // Note: badge counts use rollupAggCount at render time, not fileCount, so
  // parent folders show the sum of their descendants' files.

  function sortNodes(nodes: AggTreeNode[]) {
    nodes.sort((a, b) => a.name.localeCompare(b.name));
    for (const n of nodes) sortNodes(n.children);
  }
  roots.sort((a, b) => a.name.localeCompare(b.name));
  sortNodes(roots);

  return roots;
}

function filterAggTree(nodes: AggTreeNode[], term: string): AggTreeNode[] {
  const lowerTerm = term.toLowerCase();

  function filterNode(node: AggTreeNode): AggTreeNode | null {
    if (
      node.fullPath.toLowerCase().includes(lowerTerm) ||
      node.name.toLowerCase().includes(lowerTerm)
    ) {
      return node;
    }
    const filteredChildren = node.children
      .map(filterNode)
      .filter((n): n is AggTreeNode => n !== null);
    if (filteredChildren.length > 0) {
      return { ...node, children: filteredChildren };
    }
    return null;
  }

  return nodes.map(filterNode).filter((n): n is AggTreeNode => n !== null);
}

function getAllPaths(nodes: AggTreeNode[]): Set<string> {
  const paths = new Set<string>();
  function walk(list: AggTreeNode[]) {
    for (const n of list) {
      paths.add(n.fullPath);
      walk(n.children);
    }
  }
  walk(nodes);
  return paths;
}

/**
 * Total file count for a node, summing the node's own count plus every
 * descendant's count. Parent folders display this in the tree so the badge
 * reflects the whole subtree, not just direct children.
 */
function rollupAggCount(node: AggTreeNode): number {
  let total = node.fileCount ?? 0;
  for (const child of node.children) total += rollupAggCount(child);
  return total;
}

function FolderTreeNode({
  node,
  expanded,
  selectedPath,
  onToggle,
  onSelect,
}: {
  node: AggTreeNode;
  expanded: Set<string>;
  selectedPath: string | null;
  onToggle: (path: string) => void;
  onSelect: (path: string) => void;
}) {
  const isExpanded = expanded.has(node.fullPath);
  const isSelected = selectedPath === node.fullPath;
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <div
        className={cn(
          "flex w-full items-center rounded-md text-sm transition-colors",
          isSelected ? "bg-accent text-accent-foreground" : "hover:bg-accent/50",
        )}
        style={{ paddingLeft: `${node.depth * 20}px` }}
      >
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center"
          onClick={() => hasChildren && onToggle(node.fullPath)}
          tabIndex={hasChildren ? 0 : -1}
          aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : null}
        </button>
        <button
          type="button"
          className="flex flex-1 items-center gap-2 py-1.5 pr-2 text-left min-w-0"
          onClick={() => onSelect(node.fullPath)}
        >
          <FolderOpen className="h-4 w-4 shrink-0 text-amber-500" />
          <span className="font-medium flex-1 leading-tight">{node.name}</span>
          {(() => {
            const total = rollupAggCount(node);
            return total > 0 ? (
              <Badge variant="secondary" className="text-xs ml-auto shrink-0">
                {total}
              </Badge>
            ) : null;
          })()}
        </button>
      </div>
      {isExpanded && hasChildren && (
        <div>
          {node.children.map((child) => (
            <FolderTreeNode
              key={child.fullPath}
              node={child}
              expanded={expanded}
              selectedPath={selectedPath}
              onToggle={onToggle}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FolderTree({
  aggregates,
  isLoading,
  isError,
  error,
  onRetry,
  selectedPath,
  onSelectFolder,
}: {
  aggregates: OrgFolderAggregate[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  onRetry: () => void;
  selectedPath: string | null;
  onSelectFolder: (path: string) => void;
}) {
  const [folderSearch, setFolderSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const tree = useMemo(() => buildAggTree(aggregates), [aggregates]);
  const filtered = useMemo(
    () => (folderSearch.trim() ? filterAggTree(tree, folderSearch.trim()) : tree),
    [tree, folderSearch],
  );

  useEffect(() => {
    if (folderSearch.trim()) {
      setExpanded(getAllPaths(filtered));
    }
  }, [folderSearch, filtered]);

  function toggleExpand(path: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {["ft-sk-0", "ft-sk-1", "ft-sk-2", "ft-sk-3", "ft-sk-4"].map((k) => (
          <Skeleton key={k} className="h-9 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={onRetry} />;
  }

  return (
    <>
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search folders..."
            value={folderSearch}
            onChange={(e) => setFolderSearch(e.target.value)}
            className="h-7 pl-7 text-sm"
          />
          {folderSearch && (
            <button
              type="button"
              onClick={() => setFolderSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <FolderOpen className="mb-3 h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {folderSearch.trim() ? "No folders match your search." : "No folders found."}
            </p>
          </div>
        ) : (
          filtered.map((node) => (
            <FolderTreeNode
              key={node.fullPath}
              node={node}
              expanded={expanded}
              selectedPath={selectedPath}
              onToggle={toggleExpand}
              onSelect={onSelectFolder}
            />
          ))
        )}
      </div>
    </>
  );
}

// ── Org folder files panel (right panel) ─────────────────────────────────────

type DrillSortField = "fileName" | "projectName" | "createdAt";

function OrgFolderFilesPanel({
  folderPath,
  onOpenPreview,
}: {
  folderPath: string | null;
  onOpenPreview: (file: OrgFolderDrilldownRow) => void;
}) {
  const [sortBy, setSortBy] = useState<DrillSortField>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function handleSort(field: DrillSortField) {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
  }

  const { data, isLoading, isError, error, refetch } = useOrgFolderDrilldown(
    folderPath ?? "",
    sortBy,
    sortDir,
  );

  if (!folderPath) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-muted-foreground">
        Select a folder to view its files
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {["fp-sk-0", "fp-sk-1", "fp-sk-2", "fp-sk-3", "fp-sk-4", "fp-sk-5"].map((k) => (
          <Skeleton key={k} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
        <p className="text-sm text-muted-foreground">No files in this folder.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-muted-foreground">{folderPath}</h3>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <SortableHeader
                  label="File Name"
                  field="fileName"
                  current={sortBy}
                  dir={sortDir}
                  onSort={handleSort}
                />
              </TableHead>
              <TableHead>
                <SortableHeader
                  label="Project"
                  field="projectName"
                  current={sortBy}
                  dir={sortDir}
                  onSort={handleSort}
                />
              </TableHead>
              <TableHead className="w-28">
                <SortableHeader
                  label="Date"
                  field="createdAt"
                  current={sortBy}
                  dir={sortDir}
                  onSort={handleSort}
                />
              </TableHead>
              <TableHead className="w-20">Size</TableHead>
              <TableHead className="w-24">Type</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((file) => (
              <TableRow
                key={file.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onOpenPreview(file)}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <FileIcon mimeType={file.mimeType} />
                    <span className="truncate max-w-xs font-medium">{file.fileName}</span>
                  </div>
                </TableCell>
                <TableCell className="text-sm">{file.projectName ?? "—"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(file.createdAt)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatFileSize(file.fileSizeBytes)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {getFileTypeLabel(file.mimeType)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function DocumentsPage() {
  const navigate = useNavigate({ from: "/documents" });
  const rawSearch = useSearch({ strict: false }) as {
    tab?: string;
    search?: string;
    projectIds?: string;
    folderPaths?: string;
  };

  const activeTab = rawSearch.tab === "folders" ? "folders" : "files";
  const searchParam = rawSearch.search ?? "";
  const projectIdsParam = rawSearch.projectIds ?? "";
  const folderPathsParam = rawSearch.folderPaths ?? "";

  const selectedProjectIds = useMemo(
    () => projectIdsParam.split(",").filter(Boolean),
    [projectIdsParam],
  );
  const selectedFolderPaths = useMemo(
    () => folderPathsParam.split(",").filter(Boolean),
    [folderPathsParam],
  );

  // Controlled search input — debounced 350ms to URL
  const [searchInput, setSearchInput] = useState(searchParam);

  useEffect(() => {
    const timer = setTimeout(() => {
      navigate({
        search: (prev) => ({ ...prev, search: searchInput || "" }),
        replace: true,
      });
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput, navigate]);

  // Files tab sort state (local — not in URL)
  const [sortBy, setSortBy] = useState<OrgFileSortField>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  function handleSort(field: OrgFileSortField) {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir(field === "createdAt" ? "desc" : "asc");
    }
  }

  // Folders tab drilldown state
  const [drilldownPath, setDrilldownPath] = useState<string | null>(null);
  const [shareTarget, setShareTarget] = useState<{
    fileId: string;
    projectId: string;
    fileName: string;
  } | null>(null);

  // Preview state
  const [preview, setPreview] = useState<PreviewState | null>(null);

  async function openPreview(file: { id: string; fileName: string; mimeType: string | null }) {
    const kind = isPreviewable(file.mimeType);
    if (!kind) {
      setPreview({
        id: file.id,
        fileName: file.fileName,
        mimeType: file.mimeType,
        blobUrl: null,
        loading: false,
        error: null,
      });
      return;
    }
    setPreview({
      id: file.id,
      fileName: file.fileName,
      mimeType: file.mimeType,
      blobUrl: null,
      loading: true,
      error: null,
    });
    try {
      const res = await fetch(`/api/files/${file.id}`);
      if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
      const blob = await res.blob();
      const typedBlob =
        file.mimeType && blob.type !== file.mimeType
          ? new Blob([blob], { type: file.mimeType })
          : blob;
      const url = URL.createObjectURL(typedBlob);
      setPreview((prev) =>
        prev?.id === file.id ? { ...prev, blobUrl: url, loading: false } : prev,
      );
    } catch (err) {
      setPreview((prev) =>
        prev?.id === file.id
          ? {
              ...prev,
              loading: false,
              error: err instanceof Error ? err.message : "Failed to load preview",
            }
          : prev,
      );
    }
  }

  function closePreview() {
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  }

  // Data hooks
  const orgFilesQuery = useOrgFiles({
    search: searchParam,
    projectIds: selectedProjectIds,
    folderPaths: selectedFolderPaths,
    sortBy,
    sortDir,
    limit: 50,
  });

  const orgFoldersQuery = useOrgFolders();
  const { data: projects } = useProjects();

  const allFiles = useMemo(
    () => orgFilesQuery.data?.pages.flatMap((p) => p.data) ?? [],
    [orgFilesQuery.data],
  );

  // Infinite scroll sentinel
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !orgFilesQuery.hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !orgFilesQuery.isFetchingNextPage) {
            orgFilesQuery.fetchNextPage();
          }
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [orgFilesQuery.hasNextPage, orgFilesQuery.isFetchingNextPage, orgFilesQuery.fetchNextPage]);

  function setTab(tab: "files" | "folders") {
    navigate({
      search: (prev) => ({ ...prev, tab }),
      replace: false,
    });
  }

  function toggleProject(id: string) {
    const next = selectedProjectIds.includes(id)
      ? selectedProjectIds.filter((p) => p !== id)
      : [...selectedProjectIds, id];
    navigate({
      search: (prev) => ({ ...prev, projectIds: next.join(",") || "" }),
      replace: true,
    });
  }

  function toggleFolderPath(path: string) {
    const next = selectedFolderPaths.includes(path)
      ? selectedFolderPaths.filter((p) => p !== path)
      : [...selectedFolderPaths, path];
    navigate({
      search: (prev) => ({ ...prev, folderPaths: next.join(",") || "" }),
      replace: true,
    });
  }

  function clearAllFilters() {
    setSearchInput("");
    navigate({
      search: (prev) => ({
        tab: prev.tab,
        search: "",
        projectIds: "",
        folderPaths: "",
      }),
      replace: true,
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Documents" />

      <Tabs value={activeTab} onValueChange={(v) => setTab(v as "files" | "folders")}>
        <TabsList>
          <TabsTrigger value="files">Files</TabsTrigger>
          <TabsTrigger value="folders">Folders</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="space-y-4 mt-4">
          <FilesTabToolbar
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            projects={projects ?? []}
            selectedProjectIds={selectedProjectIds}
            onToggleProject={toggleProject}
            orgFolders={orgFoldersQuery.data ?? []}
            selectedFolderPaths={selectedFolderPaths}
            onToggleFolderPath={toggleFolderPath}
            onClearAll={clearAllFilters}
          />
          <FilesTable
            files={allFiles}
            isLoading={orgFilesQuery.isLoading}
            isError={orgFilesQuery.isError}
            error={orgFilesQuery.error}
            onRetry={() => orgFilesQuery.refetch()}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            sentinelRef={sentinelRef}
            isFetchingNextPage={orgFilesQuery.isFetchingNextPage}
            onOpenPreview={openPreview}
            onShareFile={(file) => {
              if (file.projectId) {
                setShareTarget({
                  fileId: file.id,
                  projectId: file.projectId,
                  fileName: file.fileName,
                });
              }
            }}
          />
        </TabsContent>

        <TabsContent value="folders" className="mt-4">
          <div className="flex rounded-md border overflow-hidden" style={{ minHeight: "500px" }}>
            {/* Left panel — folder tree */}
            <div className="w-[280px] shrink-0 border-r flex flex-col bg-muted/20">
              <FolderTree
                aggregates={orgFoldersQuery.data ?? []}
                isLoading={orgFoldersQuery.isLoading}
                isError={orgFoldersQuery.isError}
                error={orgFoldersQuery.error}
                onRetry={() => orgFoldersQuery.refetch()}
                selectedPath={drilldownPath}
                onSelectFolder={setDrilldownPath}
              />
            </div>
            {/* Right panel — files */}
            <div className="flex-1 overflow-auto p-4">
              <OrgFolderFilesPanel folderPath={drilldownPath} onOpenPreview={openPreview} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <FilePreviewDialog preview={preview} onClose={closePreview} />

      {shareTarget && (
        <CreateShareDialog
          open={!!shareTarget}
          onOpenChange={(open) => {
            if (!open) setShareTarget(null);
          }}
          projectId={shareTarget.projectId}
          lockedFileId={shareTarget.fileId}
          lockedFileName={shareTarget.fileName}
        />
      )}
    </div>
  );
}
